import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { PRICE_LOOKUP_KEYS, type PaymentEnv } from "@/lib/stripe";

const isKnownPlan = (priceId: string) =>
  (PRICE_LOOKUP_KEYS as readonly string[]).includes(priceId);

const DEFAULT_ORIGIN = "https://www.inquira.online";
/** Only return users to Inquira's own sites; anything else falls back to the main domain. */
function safeOrigin(input?: string): string {
  if (!input) return DEFAULT_ORIGIN;
  try {
    const u = new URL(input);
    const host = u.hostname.toLowerCase();
    const ok =
      (u.protocol === "https:" &&
        (host === "www.inquira.online" ||
          host === "inquira.online" ||
          host === "inquira.lovable.app" ||
          host.endsWith(".lovable.app"))) ||
      ((u.protocol === "http:" || u.protocol === "https:") && host === "localhost");
    return ok ? u.origin : DEFAULT_ORIGIN;
  } catch {
    return DEFAULT_ORIGIN;
  }
}

/**
 * Creates a Stripe Checkout session for the signed-in user.
 * The customer is always derived from the authenticated session, never from
 * client input, so a payment can't be attributed to another account.
 */
export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { priceId: string; origin: string; withTrial?: boolean }) => {
    if (!isKnownPlan(data.priceId)) throw new Error("Unknown plan");
    return data;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId, claims } = context;
    const { getStripeClient, resolvePriceId } = await import("@/lib/stripe.server");
    const stripe = getStripeClient();

    const price = await resolvePriceId(data.priceId);
    if (!price) return { url: null as string | null, error: "price_not_found" as const };

    // Trial eligibility is decided server-side from the account record, never from client input.
    let trialDays: number | null = null;
    if (data.withTrial) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("trial_used")
        .eq("user_id", userId)
        .maybeSingle();
      if (!profile?.trial_used) trialDays = 3;
    }

    // Reuse the customer we already know about for this user.
    const { data: existing } = await supabase
      .from("subscriptions")
      .select("stripe_customer_id")
      .eq("user_id", userId)
      .not("stripe_customer_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    let customerId = existing?.stripe_customer_id ?? undefined;
    if (!customerId) {
      const email = (claims as { email?: string } | null)?.email;
      const found = email
        ? await stripe.customers.list({ email, limit: 1 })
        : { data: [] as Array<{ id: string }> };
      customerId =
        found.data[0]?.id ??
        (await stripe.customers.create({ email, metadata: { user_id: userId } })).id;
    }

    const origin = safeOrigin(data.origin);

    const session = await stripe.checkout.sessions.create({
      mode: "subscription",
      customer: customerId,
      line_items: [{ price, quantity: 1 }],
      client_reference_id: userId,
      allow_promotion_codes: true,
      // A trial must never start without a payment method on file.
      payment_method_collection: "always" as const,
      subscription_data: {
        metadata: { user_id: userId, price_lookup_key: data.priceId },
        ...(trialDays
          ? {
              trial_period_days: trialDays,
              trial_settings: { end_behavior: { missing_payment_method: "cancel" as const } },
            }
          : {}),
      },
      metadata: { user_id: userId, price_lookup_key: data.priceId },
      success_url: `${origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${origin}/upgrade`,
    });

    return { url: session.url, trialDays, error: null as string | null };
  });

/**
 * Hosted Stripe billing portal so customers can update their payment method,
 * switch plans, cancel, or download invoices.
 */
export const createBillingPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { environment: PaymentEnv; origin?: string }) => data)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: sub } = await supabase
      .from("subscriptions")
      .select("stripe_customer_id")
      .eq("user_id", userId)
      .eq("environment", data.environment)
      .not("stripe_customer_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!sub?.stripe_customer_id) {
      return { url: null as string | null, updatePaymentUrl: null as string | null, error: "no_subscription" };
    }

    const { getStripeClient } = await import("@/lib/stripe.server");
    const origin = safeOrigin(data.origin);

    const session = await getStripeClient().billingPortal.sessions.create({
      customer: sub.stripe_customer_id,
      return_url: `${origin}/upgrade`,
    });

    return { url: session.url, updatePaymentUrl: session.url, error: null as string | null };
  });

const PLAN_ORDER: Record<string, number> = {
  inquira_pro_monthly: 1,
  inquira_pro_yearly: 2,
};

/**
 * "Restore purchases": re-reads the customer's live subscriptions from Stripe
 * and rewrites our records, then recomputes premium entitlement server-side.
 * Useful after a device change, a failed webhook, or a store-side purchase.
 */
export const restorePurchases = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { environment: PaymentEnv }) => data)
  .handler(async ({ data, context }) => {
    const { supabase, userId, claims } = context;
    const { getStripeClient } = await import("@/lib/stripe.server");
    const stripe = getStripeClient();

    const { data: known } = await supabase
      .from("subscriptions")
      .select("stripe_customer_id")
      .eq("user_id", userId)
      .not("stripe_customer_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    let customerId = known?.stripe_customer_id ?? null;
    if (!customerId) {
      const email = (claims as { email?: string } | null)?.email;
      if (email) {
        const found = await stripe.customers.list({ email, limit: 1 });
        customerId = found.data[0]?.id ?? null;
      }
    }
    if (!customerId) return { restored: 0, premium: false, error: "no_customer" as const };

    const subs = await stripe.subscriptions.list({ customer: customerId, status: "all", limit: 20 });
    let restored = 0;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    for (const sub of subs.data) {
      const item = sub.items.data[0];
      const lookupKey = item?.price?.lookup_key ?? null;
      if (!lookupKey) continue;
      const period = item as unknown as { current_period_start?: number; current_period_end?: number };
      const iso = (s?: number) => (s ? new Date(s * 1000).toISOString() : null);

      const { error } = await supabaseAdmin
        .from("subscriptions")
        .upsert(
          {
            user_id: userId,
            stripe_subscription_id: sub.id,
            stripe_customer_id: customerId,
            product_id:
              typeof item?.price?.product === "string" ? item.price.product : (item?.price?.product?.id ?? ""),
            price_id: lookupKey,
            status: sub.status,
            current_period_start: iso(period?.current_period_start),
            current_period_end: iso(period?.current_period_end),
            cancel_at_period_end: sub.cancel_at_period_end ?? false,
            environment: data.environment,
            updated_at: new Date().toISOString(),
          } as never,
          { onConflict: "stripe_subscription_id" },
        );
      if (error) {
        console.error("restorePurchases upsert failed:", error.message);
        continue;
      }
      restored += 1;
    }

    await supabaseAdmin.rpc("sync_premium_state", { _user_id: userId } as never);

    const { data: state } = await supabase.rpc("my_premium_state");
    const premium = Boolean((state as { premium_access?: boolean } | null)?.premium_access);

    return { restored, premium, error: null as string | null };
  });

/**
 * Switches the signed-in user between the monthly and yearly Pro plans.
 * Upgrades apply immediately with proration; downgrades take effect at the
 * next renewal so the customer keeps what they already paid for.
 */
export const changeSubscriptionPlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { priceId: string; environment: PaymentEnv }) => {
    if (!isKnownPlan(data.priceId)) throw new Error("Unknown plan");
    return data;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: sub } = await supabase
      .from("subscriptions")
      .select("stripe_subscription_id, price_id, status")
      .eq("user_id", userId)
      .eq("environment", data.environment)
      .not("stripe_subscription_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!sub?.stripe_subscription_id) return { ok: false, isUpgrade: false, error: "no_subscription" as const };
    if (sub.price_id === data.priceId) return { ok: false, isUpgrade: false, error: "already_on_plan" as const };

    const isUpgrade = (PLAN_ORDER[data.priceId] ?? 0) > (PLAN_ORDER[sub.price_id ?? ""] ?? 0);

    const { getStripeClient, resolvePriceId } = await import("@/lib/stripe.server");
    const stripe = getStripeClient();

    const newPrice = await resolvePriceId(data.priceId);
    if (!newPrice) return { ok: false, isUpgrade, error: "price_not_found" as const };

    const current = await stripe.subscriptions.retrieve(sub.stripe_subscription_id);
    const itemId = current.items.data[0]?.id;
    if (!itemId) return { ok: false, isUpgrade, error: "no_subscription" as const };

    await stripe.subscriptions.update(sub.stripe_subscription_id, {
      items: [{ id: itemId, price: newPrice, quantity: 1 }],
      proration_behavior: isUpgrade ? "always_invoice" : "none",
      billing_cycle_anchor: isUpgrade ? "now" : "unchanged",
      metadata: { user_id: userId, price_lookup_key: data.priceId },
    });

    return { ok: true, isUpgrade, error: null };
  });
/**
 * Cancels the signed-in user's subscription at the end of the current period.
 * The churn reason is captured client-side before this runs so we always learn
 * why someone left, even if they never open the Stripe portal.
 */
export const cancelSubscriptionAtPeriodEnd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { environment: PaymentEnv }) => data)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: sub } = await supabase
      .from("subscriptions")
      .select("stripe_subscription_id")
      .eq("user_id", userId)
      .eq("environment", data.environment)
      .not("stripe_subscription_id", "is", null)
      .in("status", ["active", "trialing", "past_due"])
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!sub?.stripe_subscription_id) {
      return { ok: false, periodEnd: null as string | null, error: "no_subscription" as const };
    }

    const { getStripeClient } = await import("@/lib/stripe.server");
    const updated = await getStripeClient().subscriptions.update(sub.stripe_subscription_id, {
      cancel_at_period_end: true,
      metadata: { user_id: userId, canceled_from: "in_app" },
    });

    const periodEnd = (updated as unknown as { current_period_end?: number }).current_period_end;

    return {
      ok: true,
      periodEnd: periodEnd ? new Date(periodEnd * 1000).toISOString() : null,
      error: null as string | null,
    };
  });
